import { useRef, useState } from "react";
import { adminApi, API } from "@/lib/api";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_BASE = API.replace(/\/api$/, "");

// Attach an image file to object storage; returns an absolute URL via onChange.
export const ImageUpload = ({ value, onChange, testId = "image-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi().post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const absolute = data.url.startsWith("http") ? data.url : `${BACKEND_BASE}${data.url}`;
      onChange(absolute);
      toast.success("Image attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="preview" className="w-20 h-24 object-cover bg-surface border border-gray-100" />
          <div className="flex flex-col gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
              data-testid="image-replace-btn"
              className="flex items-center gap-1.5 border border-gray-300 px-3 py-2 text-xs uppercase tracking-[0.1em] hover:border-pink hover:text-pink transition-colors disabled:opacity-60">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Replace
            </button>
            <button type="button" onClick={() => onChange("")} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-pink">
              <X className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid="image-upload-btn"
          className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 py-8 text-gray-500 hover:border-pink hover:text-pink transition-colors disabled:opacity-60">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
          <span className="text-xs uppercase tracking-[0.12em]">{uploading ? "Uploading..." : "Attach Image"}</span>
          <span className="text-[11px] text-gray-400">JPG, PNG, WEBP or GIF (max 8MB)</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="image-file-input" />
    </div>
  );
};
